// v10/src/components/admin/inventory/InventoryTab.js
//
// Pestaña Stock de repuestos del módulo "Mantenimiento e inventario" (A8).
// Tarea 1 (lectura): toggle entre "Por categoría" y "Por aeronave" + dashboard
// header. La carga de items vive en cada vista; este contenedor coordina el
// toggle, sostiene el state lifted (aeronave seleccionada) y lleva el polling
// del dashboard.
//
// Tarea 2 (acciones): este contenedor también orquesta los modales de
// CRUD/movimientos/fotos/serialized/compatibilidades. Cada modal exitoso
// dispara `refreshAll()` para que el dashboard y las vistas se actualicen
// sin esperar al poll.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutGrid, Plane, ShieldOff, Plus, FileDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import api from '../../../services/apiService';
import inventoryService from '../../../services/inventoryService';
import reportsService from '../../../services/reportsService';
import { POLLING_INTERVALS } from '../../../config/realtime';
import InventoryDashboardHeader from './InventoryDashboardHeader';
import ByCategoryView from './ByCategoryView';
import ByAircraftView from './ByAircraftView';
import ExportReportModal from '../../common/ExportReportModal';

import useIsMobile from '../../../hooks/useIsMobile';

import CreateInventoryItemModal from './modals/CreateInventoryItemModal';
import EditInventoryItemModal from './modals/EditInventoryItemModal';
import DeleteInventoryItemModal from './modals/DeleteInventoryItemModal';
import RegisterMovementModal from './modals/RegisterMovementModal';
import InventoryItemDetailModal from './modals/InventoryItemDetailModal';
import PhotoUploadModal from './modals/PhotoUploadModal';
import CompatibilityModal from './modals/CompatibilityModal';

const VIEW_BY_CATEGORY = 'by_category';
const VIEW_BY_AIRCRAFT = 'by_aircraft';

// Roles con acceso al módulo de mantenimiento. El backend ya los filtra,
// pero defendemos en frontend para no exponer el tab si algún día cambia.
const ALLOWED_ROLES = new Set(['admin', 'instructor', 'superadmin']);
const DELETE_ROLES  = new Set(['admin', 'superadmin']);

const InventoryTab = ({ darkMode, userRole, locale = 'es-AR', isActive = true }) => {
  const { t } = useTranslation('admin');
  const isMobile = useIsMobile();

  const [view, setView] = useState(VIEW_BY_CATEGORY);
  const [selectedAircraftId, setSelectedAircraftId] = useState(null);

  const [stats,   setStats]   = useState(null);
  const [statsError, setStatsError] = useState('');

  // Aeronaves activas de la org — compartidas con los modales (movimientos, S/N)
  const [aircraftList, setAircraftList] = useState([]);

  // Modales abiertos
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [movementItem, setMovementItem] = useState(null);
  const [detailItemId, setDetailItemId] = useState(null);
  const [detailInitialTab, setDetailInitialTab] = useState('info');
  const [photoItem, setPhotoItem] = useState(null);
  const [compatItem, setCompatItem] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  // Para forzar refresh de las vistas sin tocar el polling existente
  const [refreshKey, setRefreshKey] = useState(0);

  const pollRef    = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const allowed = ALLOWED_ROLES.has(userRole);
  const canEdit = allowed;
  const canDelete = DELETE_ROLES.has(userRole);

  // Dashboard
  const loadDashboard = useCallback(async () => {
    try {
      const res = await inventoryService.dashboard();
      const data = res?.data?.data || null;
      if (!mountedRef.current) return;
      setStats(data);
      setStatsError('');
    } catch (_err) {
      if (!mountedRef.current) return;
      setStatsError(t('inventory_error_dashboard'));
    }
  }, [t]);

  // Aeronaves activas — lazy load la primera vez que abre un modal que las usa.
  const loadAircraft = useCallback(async () => {
    try {
      const res = await api.get('/api/aircraft', { params: { is_active: 'true' } });
      if (!mountedRef.current) return;
      setAircraftList(res?.data?.data || []);
    } catch (_) {
      // silencioso — los modales muestran selectores vacíos si falla
    }
  }, []);

  useEffect(() => {
    if (!allowed) return;
    loadDashboard();
    loadAircraft();
  }, [allowed, loadDashboard, loadAircraft]);

  // Polling del dashboard — pausado cuando la pestaña no está activa o el
  // documento está oculto.
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!allowed || !isActive) return undefined;
    pollRef.current = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      loadDashboard();
    }, POLLING_INTERVALS.inventory);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [allowed, isActive, loadDashboard]);

  // Refresh global tras una acción exitosa de un modal.
  const refreshAll = useCallback(() => {
    loadDashboard();
    setRefreshKey((k) => k + 1);
  }, [loadDashboard]);

  // CTA "Ver alertas" del dashboard — abre la vista por categoría con el
  // filtro de stock bajo aplicado. Usamos remount con key para inyectar
  // initialFilters sin tocar el state interno de la vista.
  const [alertsTrigger, setAlertsTrigger] = useState(0);
  const handleShowAlerts = useCallback(() => {
    setView(VIEW_BY_CATEGORY);
    setAlertsTrigger((k) => k + 1);
  }, []);
  const initialCategoryFilters = useMemo(() => ({
    search: '', category: '', tracking_mode: '', low_stock: alertsTrigger > 0,
  }), [alertsTrigger]);

  // Las acciones de la card delegan al padre que abre el modal correspondiente.
  const handleItemAction = useCallback((action, item) => {
    if (!item) return;
    switch (action) {
      case 'view_detail':
        setDetailInitialTab('info');
        setDetailItemId(item.id);
        break;
      case 'edit':
        setEditItem(item);
        break;
      case 'delete':
        setDeleteItem(item);
        break;
      case 'movement':
        setMovementItem(item);
        break;
      case 'photo':
        setPhotoItem(item);
        break;
      case 'manage_serialized':
        // El brief permite acceder al ABM de S/N desde el kebab. Abrimos el
        // detail en la tab "S/N" para que el usuario tenga el listado y los
        // controles de agregar/editar en un solo lugar.
        setDetailInitialTab('serialized');
        setDetailItemId(item.id);
        break;
      case 'compatibilities':
        setCompatItem(item);
        break;
      default:
        break;
    }
  }, []);

  // Sin permisos: placeholder.
  if (!allowed) {
    return <UnauthorizedState darkMode={darkMode} />;
  }

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';

  const VIEWS = [
    { id: VIEW_BY_CATEGORY, label: t('inventory_view_by_category'), icon: LayoutGrid },
    { id: VIEW_BY_AIRCRAFT, label: t('inventory_view_by_aircraft'), icon: Plane },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Toggle pills + CTA "Nuevo producto" */}
      <div style={isMobile ? {
        display: 'flex', flexDirection: 'column', gap: 10,
      } : {
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div
          role="tablist"
          aria-label={t('inventory_tab_view_label')}
          style={isMobile ? {
            display: 'flex', flexWrap: 'nowrap',
            overflowX: 'auto', WebkitOverflowScrolling: 'touch',
            padding: 4, borderRadius: 14, gap: 4,
            background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
            border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
            alignSelf: 'stretch',
          } : {
            display: 'inline-flex',
            padding: 4, borderRadius: 14, gap: 4,
            background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
            border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
          }}
        >
          {VIEWS.map((v) => {
            const isSelected = v.id === view;
            const Icon = v.icon;
            return (
              <button
                key={v.id}
                type="button"
                role="tab"
                aria-selected={isSelected}
                onClick={() => setView(v.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 14px', borderRadius: 11, border: 'none', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                  background: isSelected
                    ? (darkMode ? 'rgba(255,255,255,0.10)' : 'white')
                    : 'transparent',
                  color: isSelected
                    ? (darkMode ? 'white' : '#0f172a')
                    : fgMuted,
                  boxShadow: isSelected ? '0 2px 8px rgba(15,23,42,0.08)' : 'none',
                  transition: 'all 0.18s',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  ...(isMobile ? { flex: 1, justifyContent: 'center' } : null),
                }}
              >
                <Icon style={{ width: 14, height: 14 }} />
                {v.label}
              </button>
            );
          })}
        </div>

        <div style={isMobile ? {
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
        } : {
          display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          {canEdit && (
            <motion.button
              type="button"
              onClick={() => setExportOpen(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: 12, cursor: 'pointer',
                background: darkMode ? 'rgba(14,165,233,0.18)' : 'rgba(14,165,233,0.1)',
                color: '#0ea5e9', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                border: '1px solid rgba(14,165,233,0.35)',
                ...(isMobile ? { justifyContent: 'center', width: '100%' } : null),
              }}
            >
              <FileDown style={{ width: 14, height: 14 }} />
              {t('report_button_export')}
            </motion.button>
          )}
          {canEdit && (
            <motion.button
              type="button"
              onClick={() => setCreateOpen(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: 12, border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                color: 'white', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                boxShadow: '0 8px 18px rgba(79,70,229,0.28)',
                ...(isMobile ? { justifyContent: 'center', width: '100%' } : null),
              }}
            >
              <Plus style={{ width: 14, height: 14 }} />
              {t('inventory_new_item_button')}
            </motion.button>
          )}
        </div>
      </div>

      {/* Dashboard header */}
      {stats && (
        <InventoryDashboardHeader
          stats={stats}
          darkMode={darkMode}
          locale={locale}
          onShowAlerts={handleShowAlerts}
        />
      )}
      {!stats && statsError && (
        <p style={{ margin: 0, fontSize: 12, color: '#dc2626' }}>{statsError}</p>
      )}

      {/* Vista activa */}
      <AnimatePresence mode="wait">
        <motion.div
          key={view}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.16 }}
        >
          {view === VIEW_BY_CATEGORY && (
            <ByCategoryView
              key={`category-${alertsTrigger}`}
              darkMode={darkMode}
              locale={locale}
              isActive={isActive && view === VIEW_BY_CATEGORY}
              onItemAction={handleItemAction}
              canEdit={canEdit}
              canDelete={canDelete}
              initialFilters={initialCategoryFilters}
              refreshKey={refreshKey}
            />
          )}
          {view === VIEW_BY_AIRCRAFT && (
            <ByAircraftView
              darkMode={darkMode}
              locale={locale}
              isActive={isActive && view === VIEW_BY_AIRCRAFT}
              selectedAircraftId={selectedAircraftId}
              onAircraftChange={setSelectedAircraftId}
              onItemAction={handleItemAction}
              canEdit={canEdit}
              canDelete={canDelete}
              refreshKey={refreshKey}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* ── Modales ────────────────────────────────────────────── */}
      <AnimatePresence>
        {createOpen && (
          <CreateInventoryItemModal
            isOpen={createOpen}
            darkMode={darkMode}
            onClose={() => setCreateOpen(false)}
            onSuccess={() => { refreshAll(); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editItem && (
          <EditInventoryItemModal
            isOpen={!!editItem}
            item={editItem}
            darkMode={darkMode}
            onClose={() => setEditItem(null)}
            onSuccess={() => { refreshAll(); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteItem && (
          <DeleteInventoryItemModal
            isOpen={!!deleteItem}
            item={deleteItem}
            darkMode={darkMode}
            onClose={() => setDeleteItem(null)}
            onSuccess={() => { refreshAll(); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {movementItem && (
          <RegisterMovementModal
            isOpen={!!movementItem}
            item={movementItem}
            aircraft={aircraftList}
            darkMode={darkMode}
            locale={locale}
            onClose={() => setMovementItem(null)}
            onSuccess={() => { refreshAll(); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {detailItemId && (
          <InventoryItemDetailModal
            isOpen={!!detailItemId}
            itemId={detailItemId}
            aircraft={aircraftList}
            darkMode={darkMode}
            locale={locale}
            canEdit={canEdit}
            initialTab={detailInitialTab}
            onClose={() => setDetailItemId(null)}
            onUpdated={() => { refreshAll(); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {photoItem && (
          <PhotoUploadModal
            isOpen={!!photoItem}
            item={photoItem}
            darkMode={darkMode}
            onClose={() => setPhotoItem(null)}
            onSuccess={() => { refreshAll(); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {compatItem && (
          <CompatibilityModal
            isOpen={!!compatItem}
            item={compatItem}
            darkMode={darkMode}
            onClose={() => setCompatItem(null)}
            onSuccess={() => { refreshAll(); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {exportOpen && (
          <ExportReportModal
            isOpen={exportOpen}
            title={t('report_modal_title_stock')}
            formats={['pdf', 'xlsx']}
            periodOptions={{ monthPicker: false, rangePicker: true }}
            onExport={(params) => reportsService.stockMovements(params)}
            onClose={() => setExportOpen(false)}
            darkMode={darkMode}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const UnauthorizedState = ({ darkMode }) => {
  const { t } = useTranslation('admin');
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 14, padding: '48px 24px',
        borderRadius: 20,
        border: darkMode ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
        background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 56, height: 56, borderRadius: 18,
          background: darkMode ? 'rgba(239,68,68,0.18)' : 'rgba(239,68,68,0.10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <ShieldOff style={{ width: 26, height: 26, color: '#dc2626' }} />
      </div>
      <div>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary }}>
          {t('inventory_tab_title')}
        </p>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: fgMuted, maxWidth: 460 }}>
          {t('inventory_action_placeholder')}
        </p>
      </div>
    </div>
  );
};

export default InventoryTab;
